import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Modal, Form, Row, Col } from 'react-bootstrap';
import { FiNavigation, FiRefreshCw, FiEye, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { fetchTripsRequest, createTripRequest, createSubcoveragesTripRequest, fetchSubcoveragesByPartnerRequest, updateTripRequest, deleteTripRequest } from '../../store/actions/tripsActions';
import { fetchPartnersRequest } from '../../store/actions/partnerActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import {
    COVERAGE_TYPE_OPTIONS,
    PAYMENT_SCHEDULE_OPTIONS,
    REFUND_CALCULATION_BASIS_OPTIONS,
    normalizeCoverageType,
    normalizePaymentSchedule,
    normalizeRefundCalculationBasis,
    validateRequiredSubcoverageTripFields,
} from '../../constants/tripSubcoverageCoverageType';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTrips.css';

const EMPTY_TRIPS_FILTERS = {
    category: '',
    name: '',
    activity_start_date_from: '',
    activity_start_date_to: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
    company_name: '',
    activity_code: '',
    reservation_number: '',
    trip_modified: '',
    new_trip_start_date_from: '',
    new_trip_start_date_to: '',
    new_trip_end_date_from: '',
    new_trip_end_date_to: '',
};

const DEFAULT_SUBCOVERAGE = {
    reference_id: '',
    trigger_type: 'forecast',
    effective_start_date: 1,
    effective_end_date: -1,
    trigger_low: '',
    trigger_sup: '',
    unit: 'mm',
    forecast_timing_hour: 12,
    forecast_timing_day: 0,
    observation_timing_hour: null,
    observation_timing_day: null,
    siv_ratio_maintained: '',
    siv_ratio_cancelled: '',
    type_id_number: '',
    trigger_number_hours: 1,
    prcp_trigger_hour_inf: '0,0',
    trigger_hour_sup: '',
    city: '',
    zip_code: '',
    country: '',
    activity_address: '',
    sub_coverage_start_date: '',
    sub_coverage_end_date: '',
    day_start_time: '10:00:00',
    day_end_time: '18:00:00',
    orakle_price_ratio_excl_tsca: '0,85',
    geo_longitude: '',
    geo_latitude: '',
    split_voucher_cash_maintain: false,
    split_voucher_cash_cancellation: false,
    voucher_share_maintain: '0,0',
    voucher_share_cancellation: '0,0',
    cash_share_maintain: '0,0',
    cash_share_cancellation: '0,0',
    is_lump_sum: false,
    lump_sum_maintain: '',
    lump_sum_cancellation: '',
    maintain_data_source: 'ECMWF',
    cancellation_data_source: 'ECMWF',
    distribution_type: 'inclusion',
    coverage_type: '',
    payment_schedule: 'end_of_stay',
    refund_calculation_basis: '',
    weather_parameter: ['precipitation'],
    category: '',
    prcp_is_cumulated: false,
    reference_name: '',
    is_visible_reference: true,
};

const appendActiveFiltersToParams = (params, activeFilters) => {
    Object.keys(activeFilters).forEach((key) => {
        const value = activeFilters[key];
        if (key === 'trip_modified') {
            if (value === 'true' || value === 'false') {
                params[key] = value;
            }
            return;
        }
        if (value != null && String(value).trim() !== '') {
            params[key] = String(value).trim();
        }
    });
};

const AdminTrips = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const tripsState = useSelector((state) => state.trips.all);
    const { create, createSubcoverages, subcoveragesByPartner, delete: deleteState } = useSelector((state) => state.trips);
    const { partners } = useSelector((state) => state.partners);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('trips', EMPTY_TRIPS_FILTERS, {
        persistWhen: isAuthenticated,
        withCompanyPartnerSearch: true,
    });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCreateSubcoveragesModal, setShowCreateSubcoveragesModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTripId, setSelectedTripId] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);
    const [formData, setFormData] = useState({
        partner_id: '',
        name: '',
        category: '',
        activity_start_date: '',
        activity_end_date: '',
        activity_price: '',
        reservation_number: '',
        combined_addres: '',
    });
    const [subcoveragesFormData, setSubcoveragesFormData] = useState({
        partner_id: '',
        subcoverages: [{ ...DEFAULT_SUBCOVERAGE }],
    });

    // Charger les séjours avec pagination et filtres
    const loadTrips = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        appendActiveFiltersToParams(params, activeFilters);

        dispatch(fetchTripsRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadTrips(currentPage);
            if (!partners.data) {
                dispatch(fetchPartnersRequest());
            }
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (error) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };

        if (isAuthenticated) {
            loadFilterPartners();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        loadTrips(currentPage);
    }, [currentPage]);

    useEffect(() => {
        if (create.success) {
            setShowCreateModal(false);
            setSuccessMessage('Séjour créé avec succès !');
            const resetForm = {
                partner_id: '',
                name: '',
                category: '',
                activity_start_date: '',
                activity_end_date: '',
                activity_price: '',
                reservation_number: '',
                combined_addres: '',
            };
            setFormData(resetForm);
            loadTrips(currentPage);
            // Supprimer le message après 5 secondes
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [create.success, currentPage, loadTrips]);

    useEffect(() => {
        if (createSubcoverages.success) {
            setShowCreateSubcoveragesModal(false);
            setSuccessMessage('Sous-couvertures créées avec succès !');
            setSubcoveragesFormData({
                partner_id: '',
                subcoverages: [{ ...DEFAULT_SUBCOVERAGE }],
            });
            loadTrips(currentPage);
            // Supprimer le message après 5 secondes
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [createSubcoverages.success, currentPage, loadTrips]);

    // Extraire les données et métadonnées de la réponse API
    const tripsData = tripsState?.data?.data || tripsState?.data?.activities || tripsState?.data || [];
    const tripsArray = Array.isArray(tripsData) ? tripsData : [];
    const meta = tripsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + tripsArray.length, totalItems);

    const handleRefresh = () => {
        loadTrips(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadTrips(1, nextFilters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_TRIPS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadTrips(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    const handleDetails = (activityId) => {
        navigate(`/admin/trips/${activityId}`);
    };

    const handleDelete = (tripId) => {
        setSelectedTripId(tripId);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (selectedTripId) {
            dispatch(deleteTripRequest(selectedTripId));
        }
    };

    const handleCreateTrip = () => {
        if (!formData.partner_id) {
            alert('Veuillez sélectionner un partenaire');
            return;
        }
        const tripData = {
            name: formData.name,
            category: formData.category,
            activity_start_date: formData.activity_start_date,
            activity_end_date: formData.activity_end_date,
            activity_price: formData.activity_price ? parseFloat(formData.activity_price) : null,
            reservation_number: formData.reservation_number,
            combined_addres: formData.combined_addres,
        };
        dispatch(createTripRequest(formData.partner_id, tripData));
    };

    const handleCreateSubcoverages = () => {
        if (!subcoveragesFormData.partner_id) {
            alert('Veuillez sélectionner un partenaire');
            return;
        }
        if (subcoveragesFormData.subcoverages.length === 0) {
            alert('Veuillez ajouter au moins une sous-couverture');
            return;
        }

        const validationMessages = [];
        subcoveragesFormData.subcoverages.forEach((sub, index) => {
            const errors = validateRequiredSubcoverageTripFields(sub);
            if (Object.keys(errors).length > 0) {
                validationMessages.push(
                    `Sous-couverture ${index + 1}:\n${Object.values(errors).join('\n')}`
                );
            }
        });
        if (validationMessages.length > 0) {
            alert(validationMessages.join('\n\n'));
            return;
        }

        const subcoveragesData = {
            subcoverages_trip: subcoveragesFormData.subcoverages.map((sub) => ({
                subcoverage_trip: {
                    reference_id: sub.reference_id || undefined,
                    trigger_type: sub.trigger_type,
                    effective_start_date: sub.effective_start_date,
                    effective_end_date: sub.effective_end_date,
                    trigger_low: sub.trigger_low || '',
                    trigger_sup: sub.trigger_sup || '',
                    unit: sub.unit,
                    forecast_timing_hour: sub.forecast_timing_hour,
                    forecast_timing_day: sub.forecast_timing_day,
                    observation_timing_hour: sub.observation_timing_hour,
                    observation_timing_day: sub.observation_timing_day,
                    siv_ratio_maintained: sub.siv_ratio_maintained || '',
                    siv_ratio_cancelled: sub.siv_ratio_cancelled || '',
                    type_id_number: sub.type_id_number || '',
                    trigger_number_hours: sub.trigger_number_hours,
                    prcp_trigger_hour_inf: sub.prcp_trigger_hour_inf,
                    trigger_hour_sup: sub.trigger_hour_sup || '',
                    city: sub.city,
                    zip_code: sub.zip_code,
                    country: sub.country,
                    activity_address: sub.activity_address,
                    sub_coverage_start_date: sub.sub_coverage_start_date,
                    sub_coverage_end_date: sub.sub_coverage_end_date,
                    day_start_time: sub.day_start_time,
                    day_end_time: sub.day_end_time,
                    orakle_price_ratio_excl_tsca: sub.orakle_price_ratio_excl_tsca,
                    geo_longitude: sub.geo_longitude,
                    geo_latitude: sub.geo_latitude,
                    split_voucher_cash_maintain: sub.split_voucher_cash_maintain,
                    split_voucher_cash_cancellation: sub.split_voucher_cash_cancellation,
                    voucher_share_maintain: sub.voucher_share_maintain,
                    voucher_share_cancellation: sub.voucher_share_cancellation,
                    cash_share_maintain: sub.cash_share_maintain,
                    cash_share_cancellation: sub.cash_share_cancellation,
                    is_lump_sum: sub.is_lump_sum,
                    lump_sum_maintain: sub.lump_sum_maintain || '',
                    lump_sum_cancellation: sub.lump_sum_cancellation || '',
                    maintain_data_source: sub.maintain_data_source,
                    cancellation_data_source: sub.cancellation_data_source,
                    distribution_type: sub.distribution_type,
                    coverage_type: normalizeCoverageType(sub.coverage_type),
                    payment_schedule: normalizePaymentSchedule(sub.payment_schedule),
                    refund_calculation_basis: normalizeRefundCalculationBasis(sub.refund_calculation_basis),
                    weather_parameter: Array.isArray(sub.weather_parameter)
                        ? sub.weather_parameter.filter(Boolean)
                        : ['precipitation'],
                },
                reference: {
                    partner_id: subcoveragesFormData.partner_id,
                    category: sub.category,
                    prcp_is_cumulated: sub.prcp_is_cumulated,
                    name: sub.reference_name,
                    is_visible_reference: sub.is_visible_reference,
                },
            })),
        };

        dispatch(createSubcoveragesTripRequest(subcoveragesFormData.partner_id, subcoveragesData));
    };


    const addSubcoverage = () => {
        setSubcoveragesFormData({
            ...subcoveragesFormData,
            subcoverages: [
                ...subcoveragesFormData.subcoverages,
                { ...DEFAULT_SUBCOVERAGE },
            ],
        });
    };

    const removeSubcoverage = (index) => {
        setSubcoveragesFormData({
            ...subcoveragesFormData,
            subcoverages: subcoveragesFormData.subcoverages.filter((_, i) => i !== index),
        });
    };

    const updateSubcoverage = (index, field, value) => {
        const updated = [...subcoveragesFormData.subcoverages];
        updated[index] = { ...updated[index], [field]: value };
        setSubcoveragesFormData({ ...subcoveragesFormData, subcoverages: updated });
    };

    const getPartnersArray = () => {
        if (!partners.data) return [];
        const data = partners.data;
        if (Array.isArray(data)) return data;
        if (data?.data && Array.isArray(data.data)) return data.data;
        if (data?.users) return data.users;
        if (data?.partners) return data.partners;
        if (data?.data?.users) return data.data.users;
        if (data?.data?.partners) return data.data.partners;
        return [];
    };

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    // Utiliser directement les séjours de l'API (pas de filtrage côté client)
    const currentTrips = tripsArray;

    const getField = (obj, keys, fallback = 'N/A') => {
        for (const k of keys) {
            if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
        }
        return fallback;
    };

    return (
        <div className="dashboard-page admin-trips-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiNavigation className="me-2" />
                                Gestion des séjours
                            </h2>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="primary" onClick={() => {
                                setShowCreateModal(true);
                                setSuccessMessage(null);
                            }}>
                                <FiPlus className="me-2" /> Créer un séjour
                            </Button>
                        </div>
                    </div>

                    {tripsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {tripsState.error}
                        </Alert>
                    )}
                    {(create.error || createSubcoverages.error || deleteState.error) && (
                        <Alert variant="danger" className="mt-3">
                            {create.error || createSubcoverages.error || deleteState.error}
                        </Alert>
                    )}
                    {successMessage && (
                        <Alert variant="success" className="mt-3" dismissible onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Catégorie</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="category"
                                            value={filters.category}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: Camping, Event"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={filters.name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom du séjour"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code activité</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="activity_code"
                                            value={filters.activity_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: trip_123456"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={filterPartnersLoading}
                                        error={filterPartnersError}
                                        onKeyDown={handleKeyPress}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date_from"
                                            value={filters.activity_start_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date_to"
                                            value={filters.activity_start_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_from"
                                            value={filters.activity_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date fin (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_to"
                                            value={filters.activity_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Dates séjour modifiées</Form.Label>
                                        <Form.Select
                                            name="trip_modified"
                                            value={filters.trip_modified}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">Tous</option>
                                            <option value="true">Oui</option>
                                            <option value="false">Non</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouveau début séjour (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_start_date_from"
                                            value={filters.new_trip_start_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouveau début séjour (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_start_date_to"
                                            value={filters.new_trip_start_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouvelle fin séjour (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_end_date_from"
                                            value={filters.new_trip_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nouvelle fin séjour (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="new_trip_end_date_to"
                                            value={filters.new_trip_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={tripsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${tripsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {tripsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des séjours...</p>
                        </div>
                    ) : tripsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{tripsState.error}</Alert>
                        </div>
                    ) : currentTrips.length === 0 ? (
                        <div className="text-center py-5">
                            <FiNavigation size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucun séjour disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Nom</th>
                                            <th>Date Début</th>
                                            <th>Date Fin</th>
                                            <th>Ville</th>
                                            <th>Pays</th>
                                            <th>Partenaire</th>
                                            <th>Catégorie</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentTrips) && currentTrips.length > 0 ? (
                                            currentTrips.map((item, idx) => {
                                                const activityId = item.activity_id || item.id || idx;
                                                const partnerInfo = item.partner_info || {};
                                                return (
                                                    <tr key={activityId}>
                                                        <td>{getField(item, ['name'])}</td>
                                                        <td>{getField(item, ['activity_start_date', 'new_trip_start_date'])}</td>
                                                        <td>{getField(item, ['activity_end_date', 'new_trip_end_date'])}</td>
                                                        <td>{getField(item, ['activity_city', 'supplier_city'])}</td>
                                                        <td>{getField(item, ['activity_country', 'supplier_country'])}</td>
                                                        <td>{getField(partnerInfo, ['company_name', 'first_name', 'last_name'])}</td>
                                                        <td>{getField(item, ['category'])}</td>
                                                        <td className="text-end col-actions">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleDetails(activityId)}
                                                                title="Détails"
                                                                aria-label="Détails"
                                                                className="me-2"
                                                            >
                                                                <FiEye />
                                                            </Button>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => navigate(`/admin/trips/edit/${activityId}`)}
                                                                title="Modifier"
                                                                aria-label="Modifier"
                                                                className="me-2"
                                                            >
                                                                <FiEdit />
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleDelete(activityId)}
                                                                title="Supprimer"
                                                                aria-label="Supprimer"
                                                            >
                                                                <FiTrash2 />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center">
                                                    Aucun séjour trouvé
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} séjour{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadTrips(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadTrips(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadTrips(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadTrips(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadTrips(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadTrips(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadTrips(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Create Trip Modal */}
            <Modal show={showCreateModal} onHide={() => {
                setShowCreateModal(false);
                setFormData({
                    partner_id: '',
                    name: '',
                    category: '',
                    activity_start_date: '',
                    activity_end_date: '',
                    activity_price: '',
                    reservation_number: '',
                    combined_addres: '',
                });
            }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Créer un nouveau séjour</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {create.error && <Alert variant="danger" className="mb-3">{create.error}</Alert>}
                    {subcoveragesByPartner.error && (
                        <Alert variant="warning" className="mb-3" dismissible onClose={() => {
                            // Réinitialiser l'erreur en rechargeant sans erreur
                            if (formData.partner_id) {
                                dispatch(fetchSubcoveragesByPartnerRequest(formData.partner_id));
                            }
                        }}>
                            <strong>Attention:</strong> {subcoveragesByPartner.error}
                        </Alert>
                    )}
                    {(() => {
                        // Validation des dates
                        const startDate = formData.activity_start_date;
                        const endDate = formData.activity_end_date;
                        if (startDate && endDate) {
                            const parseDate = (dateStr) => {
                                if (dateStr.includes('/')) {
                                    const parts = dateStr.split('/');
                                    if (parts.length === 3) {
                                        return new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}T00:00:00`);
                                    }
                                }
                                return new Date(dateStr + 'T00:00:00');
                            };
                            const start = parseDate(startDate);
                            const end = parseDate(endDate);
                            if (start > end) {
                                return (
                                    <Alert variant="danger" className="mb-3">
                                        La date de début doit être inférieure ou égale à la date de fin.
                                    </Alert>
                                );
                            }
                        }
                        return null;
                    })()}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Partenaire *</Form.Label>
                            <Form.Select
                                value={formData.partner_id}
                                onChange={(e) => {
                                    const partnerId = e.target.value;
                                    // Réinitialiser les champs dépendants du partenaire
                                    setFormData({
                                        ...formData,
                                        partner_id: partnerId,
                                        name: '',
                                        category: '',
                                        combined_addres: '',
                                    });
                                    // Charger les sous-couvertures du partenaire pour récupérer les valeurs
                                    if (partnerId) {
                                        dispatch(fetchSubcoveragesByPartnerRequest(partnerId));
                                    }
                                }}
                                required
                            >
                                <option value="">Sélectionner un partenaire</option>
                                {getPartnersArray().map((partner) => {
                                    const partnerId = partner.user_uuid || partner.partner_id || partner.id;
                                    const partnerName = partner.company_name || `${partner.first_name || ''} ${partner.last_name || ''}`.trim() || partner.email || 'N/A';
                                    return (
                                        <option key={partnerId} value={partnerId}>
                                            {partnerName}
                                        </option>
                                    );
                                })}
                            </Form.Select>
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom *</Form.Label>
                                    <Form.Select
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        disabled={!formData.partner_id || subcoveragesByPartner.loading}
                                    >
                                        <option value="">
                                            {!formData.partner_id
                                                ? 'Sélectionnez d\'abord un partenaire'
                                                : subcoveragesByPartner.loading
                                                    ? 'Chargement...'
                                                    : 'Sélectionner un nom'}
                                        </option>
                                        {(() => {
                                            // N'afficher les options que si un partenaire est sélectionné et les données sont chargées
                                            if (!formData.partner_id || subcoveragesByPartner.loading || !subcoveragesByPartner.data?.subcoverages_trip) {
                                                return null;
                                            }
                                            // Extraire les noms uniques depuis les sous-couvertures récupérées
                                            const names = [];
                                            const subcoverages = subcoveragesByPartner.data.subcoverages_trip;
                                            subcoverages.forEach((item) => {
                                                const name = item?.reference?.name;
                                                if (name && !names.includes(name)) {
                                                    names.push(name);
                                                }
                                            });
                                            return names.map((name) => (
                                                <option key={name} value={name}>
                                                    {name}
                                                </option>
                                            ));
                                        })()}
                                    </Form.Select>
                                    <Form.Text className={`${subcoveragesByPartner.error ? 'text-danger' : 'text-muted'}`}>
                                        {!formData.partner_id
                                            ? 'Sélectionnez un partenaire'
                                            : subcoveragesByPartner.loading
                                                ? 'Chargement...'
                                                : subcoveragesByPartner.error
                                                    ? 'Erreur: Impossible de charger les données'
                                                    : 'Noms disponibles'}
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Catégorie *</Form.Label>
                                    <Form.Select
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        required
                                        disabled={!formData.partner_id || subcoveragesByPartner.loading}
                                    >
                                        <option value="">
                                            {!formData.partner_id
                                                ? 'Sélectionnez d\'abord un partenaire'
                                                : subcoveragesByPartner.loading
                                                    ? 'Chargement...'
                                                    : 'Sélectionner une catégorie'}
                                        </option>
                                        {(() => {
                                            // N'afficher les options que si un partenaire est sélectionné et les données sont chargées
                                            if (!formData.partner_id || subcoveragesByPartner.loading || !subcoveragesByPartner.data?.subcoverages_trip) {
                                                return null;
                                            }
                                            // Extraire les catégories uniques depuis les sous-couvertures récupérées
                                            const categories = [];
                                            const subcoverages = subcoveragesByPartner.data.subcoverages_trip;
                                            subcoverages.forEach((item) => {
                                                const category = item?.reference?.category;
                                                if (category && !categories.includes(category)) {
                                                    categories.push(category);
                                                }
                                            });
                                            return categories.map((cat) => (
                                                <option key={cat} value={cat}>
                                                    {cat}
                                                </option>
                                            ));
                                        })()}
                                    </Form.Select>
                                    <Form.Text className={`${subcoveragesByPartner.error ? 'text-danger' : 'text-muted'}`}>
                                        {!formData.partner_id
                                            ? 'Sélectionnez un partenaire'
                                            : subcoveragesByPartner.loading
                                                ? 'Chargement...'
                                                : subcoveragesByPartner.error
                                                    ? 'Erreur: Impossible de charger les données'
                                                    : 'Catégories disponibles'}
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date de début *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={(() => {
                                            // Convertir le format DD/MM/YYYY en format YYYY-MM-DD pour l'input date
                                            if (!formData.activity_start_date) return '';
                                            // Si le format est DD/MM/YYYY, le convertir en YYYY-MM-DD
                                            if (formData.activity_start_date.includes('/')) {
                                                const parts = formData.activity_start_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            // Si déjà au format YYYY-MM-DD, retourner tel quel
                                            return formData.activity_start_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            // Convertir YYYY-MM-DD en DD/MM/YYYY pour le stockage
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                const newStartDate = `${day}/${month}/${year}`;
                                                setFormData({ ...formData, activity_start_date: newStartDate });

                                                // Si la date de fin est antérieure à la nouvelle date de début, la réinitialiser
                                                if (formData.activity_end_date) {
                                                    const parseDate = (dateStr) => {
                                                        if (dateStr.includes('/')) {
                                                            const parts = dateStr.split('/');
                                                            if (parts.length === 3) {
                                                                return new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}T00:00:00`);
                                                            }
                                                        }
                                                        return new Date(dateStr + 'T00:00:00');
                                                    };
                                                    const newStart = parseDate(newStartDate);
                                                    const end = parseDate(formData.activity_end_date);
                                                    if (newStart > end) {
                                                        setFormData(prev => ({ ...prev, activity_start_date: newStartDate, activity_end_date: '' }));
                                                    }
                                                }
                                            } else {
                                                setFormData({ ...formData, activity_start_date: '' });
                                            }
                                        }}
                                        max={(() => {
                                            // Définir la date maximale comme la date de fin
                                            if (formData.activity_end_date) {
                                                const parseDate = (dateStr) => {
                                                    if (dateStr.includes('/')) {
                                                        const parts = dateStr.split('/');
                                                        if (parts.length === 3) {
                                                            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                        }
                                                    }
                                                    return dateStr;
                                                };
                                                return parseDate(formData.activity_end_date);
                                            }
                                            return '';
                                        })()}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date de fin *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={(() => {
                                            // Convertir le format DD/MM/YYYY en format YYYY-MM-DD pour l'input date
                                            if (!formData.activity_end_date) return '';
                                            // Si le format est DD/MM/YYYY, le convertir en YYYY-MM-DD
                                            if (formData.activity_end_date.includes('/')) {
                                                const parts = formData.activity_end_date.split('/');
                                                if (parts.length === 3) {
                                                    return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                }
                                            }
                                            // Si déjà au format YYYY-MM-DD, retourner tel quel
                                            return formData.activity_end_date;
                                        })()}
                                        onChange={(e) => {
                                            const dateValue = e.target.value;
                                            // Convertir YYYY-MM-DD en DD/MM/YYYY pour le stockage
                                            if (dateValue) {
                                                const date = new Date(dateValue + 'T00:00:00');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const year = date.getFullYear();
                                                setFormData({ ...formData, activity_end_date: `${day}/${month}/${year}` });
                                            } else {
                                                setFormData({ ...formData, activity_end_date: '' });
                                            }
                                        }}
                                        min={(() => {
                                            // Définir la date minimale comme la date de début
                                            if (formData.activity_start_date) {
                                                const parseDate = (dateStr) => {
                                                    if (dateStr.includes('/')) {
                                                        const parts = dateStr.split('/');
                                                        if (parts.length === 3) {
                                                            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                                        }
                                                    }
                                                    return dateStr;
                                                };
                                                return parseDate(formData.activity_start_date);
                                            }
                                            return '';
                                        })()}
                                        required
                                    />
                                    {(() => {
                                        // Validation de la date de fin
                                        const startDate = formData.activity_start_date;
                                        const endDate = formData.activity_end_date;
                                        if (startDate && endDate) {
                                            const parseDate = (dateStr) => {
                                                if (dateStr.includes('/')) {
                                                    const parts = dateStr.split('/');
                                                    if (parts.length === 3) {
                                                        return new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}T00:00:00`);
                                                    }
                                                }
                                                return new Date(dateStr + 'T00:00:00');
                                            };
                                            const start = parseDate(startDate);
                                            const end = parseDate(endDate);
                                            if (start > end) {
                                                return (
                                                    <Form.Text className="text-danger">
                                                        La date de fin doit être supérieure ou égale à la date de début.
                                                    </Form.Text>
                                                );
                                            }
                                        }
                                        return null;
                                    })()}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Prix</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.activity_price}
                                onChange={(e) => setFormData({ ...formData, activity_price: e.target.value })}
                                placeholder="Ex: 122"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Numéro de réservation</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.reservation_number}
                                onChange={(e) => setFormData({ ...formData, reservation_number: e.target.value })}
                                placeholder="Ex: 334445GGTHTE"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Adresse combinée</Form.Label>
                            <Form.Select
                                value={formData.combined_addres}
                                onChange={(e) => setFormData({ ...formData, combined_addres: e.target.value })}
                                disabled={!formData.partner_id || subcoveragesByPartner.loading}
                            >
                                <option value="">
                                    {!formData.partner_id
                                        ? 'Sélectionnez d\'abord un partenaire'
                                        : subcoveragesByPartner.loading
                                            ? 'Chargement...'
                                            : 'Sélectionner une adresse'}
                                </option>
                                {(() => {
                                    // N'afficher les options que si un partenaire est sélectionné et les données sont chargées
                                    if (!formData.partner_id || subcoveragesByPartner.loading || !subcoveragesByPartner.data?.subcoverages_trip) {
                                        return null;
                                    }
                                    // Extraire les adresses combinées depuis les sous-couvertures récupérées
                                    const addresses = [];
                                    const subcoverages = subcoveragesByPartner.data.subcoverages_trip;
                                    subcoverages.forEach((item) => {
                                        const subcoverage = item?.subcoverage_trip;
                                        if (subcoverage?.activity_address && subcoverage?.zip_code && subcoverage?.city && subcoverage?.country) {
                                            // Formater : activity_address,zip_code,city,country (pays en minuscules)
                                            const formattedAddress = `${subcoverage.activity_address},${subcoverage.zip_code},${subcoverage.city},${subcoverage.country.toLowerCase()}`;
                                            if (!addresses.includes(formattedAddress)) {
                                                addresses.push(formattedAddress);
                                            }
                                        }
                                    });
                                    return addresses.map((address, index) => (
                                        <option key={index} value={address}>
                                            {address}
                                        </option>
                                    ));
                                })()}
                            </Form.Select>
                            <Form.Text className={`${subcoveragesByPartner.error ? 'text-danger' : 'text-muted'}`}>
                                {!formData.partner_id
                                    ? 'Sélectionnez un partenaire'
                                    : subcoveragesByPartner.loading
                                        ? 'Chargement des adresses...'
                                        : subcoveragesByPartner.error
                                            ? 'Erreur: Impossible de charger les données'
                                            : 'Adresses disponibles pour ce partenaire'}
                            </Form.Text>
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={create.loading}>
                        Annuler
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            // Vérifier la validation des dates avant de créer
                            const startDate = formData.activity_start_date;
                            const endDate = formData.activity_end_date;
                            if (startDate && endDate) {
                                const parseDate = (dateStr) => {
                                    if (dateStr.includes('/')) {
                                        const parts = dateStr.split('/');
                                        if (parts.length === 3) {
                                            return new Date(`${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}T00:00:00`);
                                        }
                                    }
                                    return new Date(dateStr + 'T00:00:00');
                                };
                                const start = parseDate(startDate);
                                const end = parseDate(endDate);
                                if (start > end) {
                                    alert('La date de début doit être inférieure ou égale à la date de fin.');
                                    return;
                                }
                            }
                            handleCreateTrip();
                        }}
                        disabled={create.loading || !formData.partner_id || !formData.name || !formData.category || !formData.activity_start_date || !formData.activity_end_date}
                    >
                        {create.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Création...
                            </>
                        ) : (
                            <>
                                <FiPlus className="me-2" />
                                Créer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create Subcoverages Modal */}
            <Modal show={showCreateSubcoveragesModal} onHide={() => {
                setShowCreateSubcoveragesModal(false);
                setSubcoveragesFormData({
                    partner_id: '',
                    subcoverages: [{ ...DEFAULT_SUBCOVERAGE }],
                });
            }} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Créer des sous-couvertures de séjour</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                    {createSubcoverages.error && <Alert variant="danger" className="mb-3">{createSubcoverages.error}</Alert>}
                    <Form>
                        <Form.Group className="mb-4">
                            <Form.Label>Partenaire *</Form.Label>
                            <Form.Select
                                value={subcoveragesFormData.partner_id}
                                onChange={(e) => setSubcoveragesFormData({ ...subcoveragesFormData, partner_id: e.target.value })}
                                required
                            >
                                <option value="">Sélectionner un partenaire</option>
                                {getPartnersArray().map((partner) => {
                                    const partnerId = partner.user_uuid || partner.partner_id || partner.id;
                                    const partnerName = partner.company_name || `${partner.first_name || ''} ${partner.last_name || ''}`.trim() || partner.email || 'N/A';
                                    return (
                                        <option key={partnerId} value={partnerId}>
                                            {partnerName}
                                        </option>
                                    );
                                })}
                            </Form.Select>
                        </Form.Group>

                        {subcoveragesFormData.subcoverages.map((sub, index) => (
                            <Card key={index} className="mb-4">
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <strong>Sous-couverture {index + 1}</strong>
                                    {subcoveragesFormData.subcoverages.length > 1 && (
                                        <Button variant="danger" size="sm" onClick={() => removeSubcoverage(index)}>
                                            Supprimer
                                        </Button>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Reference ID</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.reference_id}
                                                    onChange={(e) => updateSubcoverage(index, 'reference_id', e.target.value)}
                                                    placeholder="Ex: a70d2b2c-8842-4abe-b694-5eb59c85327e"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Nom de référence *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.reference_name}
                                                    onChange={(e) => updateSubcoverage(index, 'reference_name', e.target.value)}
                                                    placeholder="Ex: SARL DU MAS2"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Type de déclenchement *</Form.Label>
                                                <Form.Select
                                                    value={sub.trigger_type}
                                                    onChange={(e) => updateSubcoverage(index, 'trigger_type', e.target.value)}
                                                    required
                                                >
                                                    <option value="forecast">Forecast</option>
                                                    <option value="observation">Observation</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Catégorie *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.category}
                                                    onChange={(e) => updateSubcoverage(index, 'category', e.target.value)}
                                                    placeholder="Ex: Camping"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Unité *</Form.Label>
                                                <Form.Select
                                                    value={sub.unit}
                                                    onChange={(e) => updateSubcoverage(index, 'unit', e.target.value)}
                                                    required
                                                >
                                                    <option value="mm">mm</option>
                                                    <option value="km/h">km/h</option>
                                                    <option value="°C">°C</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Date début (début effectif)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={sub.effective_start_date}
                                                    onChange={(e) => updateSubcoverage(index, 'effective_start_date', parseInt(e.target.value) || 0)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Date fin (fin effective)</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={sub.effective_end_date}
                                                    onChange={(e) => updateSubcoverage(index, 'effective_end_date', parseInt(e.target.value) || 0)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Heure timing forecast</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={sub.forecast_timing_hour}
                                                    onChange={(e) => updateSubcoverage(index, 'forecast_timing_hour', parseInt(e.target.value) || null)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Jour timing forecast</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={sub.forecast_timing_day}
                                                    onChange={(e) => updateSubcoverage(index, 'forecast_timing_day', parseInt(e.target.value) || 0)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Ville *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.city}
                                                    onChange={(e) => updateSubcoverage(index, 'city', e.target.value)}
                                                    placeholder="Ex: Paris"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Code postal *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.zip_code}
                                                    onChange={(e) => updateSubcoverage(index, 'zip_code', e.target.value)}
                                                    placeholder="Ex: 75001"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Pays *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.country}
                                                    onChange={(e) => updateSubcoverage(index, 'country', e.target.value)}
                                                    placeholder="Ex: France"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Adresse activité *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.activity_address}
                                                    onChange={(e) => updateSubcoverage(index, 'activity_address', e.target.value)}
                                                    placeholder="Ex: 123 rue de la paix"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Date début sous-couverture *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.sub_coverage_start_date}
                                                    onChange={(e) => updateSubcoverage(index, 'sub_coverage_start_date', e.target.value)}
                                                    placeholder="Ex: 01/04/2025"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Date fin sous-couverture *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.sub_coverage_end_date}
                                                    onChange={(e) => updateSubcoverage(index, 'sub_coverage_end_date', e.target.value)}
                                                    placeholder="Ex: 30/12/2025"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Ratio SIV maintenu</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.siv_ratio_maintained}
                                                    onChange={(e) => updateSubcoverage(index, 'siv_ratio_maintained', e.target.value)}
                                                    placeholder="Ex: 50,3"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Ratio SIV annulé</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.siv_ratio_cancelled}
                                                    onChange={(e) => updateSubcoverage(index, 'siv_ratio_cancelled', e.target.value)}
                                                    placeholder="Ex: 100,0"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Partage voucher maintenu</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.voucher_share_maintain}
                                                    onChange={(e) => updateSubcoverage(index, 'voucher_share_maintain', e.target.value)}
                                                    placeholder="Ex: 20,0"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Partage cash maintenu</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sub.cash_share_maintain}
                                                    onChange={(e) => updateSubcoverage(index, 'cash_share_maintain', e.target.value)}
                                                    placeholder="Ex: 30,0"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Type de couverture *</Form.Label>
                                                <Form.Select
                                                    value={sub.coverage_type || ''}
                                                    onChange={(e) => updateSubcoverage(index, 'coverage_type', e.target.value)}
                                                    required
                                                >
                                                    <option value="">— Sélectionner —</option>
                                                    {COVERAGE_TYPE_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Type de distribution *</Form.Label>
                                                <Form.Select
                                                    value={sub.distribution_type}
                                                    onChange={(e) => updateSubcoverage(index, 'distribution_type', e.target.value)}
                                                    required
                                                >
                                                    <option value="inclusion">Inclusion</option>
                                                    <option value="option">Option</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Plan de paiement *</Form.Label>
                                                <Form.Select
                                                    value={sub.payment_schedule}
                                                    onChange={(e) => updateSubcoverage(index, 'payment_schedule', e.target.value)}
                                                    required
                                                >
                                                    {PAYMENT_SCHEDULE_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Base de calcul du remboursement *</Form.Label>
                                                <Form.Select
                                                    value={sub.refund_calculation_basis || ''}
                                                    onChange={(e) => updateSubcoverage(index, 'refund_calculation_basis', e.target.value)}
                                                    required
                                                >
                                                    <option value="">— Sélectionner —</option>
                                                    {REFUND_CALCULATION_BASIS_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Split voucher/cash maintenu"
                                                    checked={sub.split_voucher_cash_maintain}
                                                    onChange={(e) => updateSubcoverage(index, 'split_voucher_cash_maintain', e.target.checked)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Check
                                                    type="checkbox"
                                                    label="Somme forfaitaire"
                                                    checked={sub.is_lump_sum}
                                                    onChange={(e) => updateSubcoverage(index, 'is_lump_sum', e.target.checked)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))}

                        <Button variant="outline-primary" onClick={addSubcoverage} className="mb-3">
                            <FiPlus className="me-2" /> Ajouter une sous-couverture
                        </Button>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateSubcoveragesModal(false)} disabled={createSubcoverages.loading}>
                        Annuler
                    </Button>
                    <Button variant="primary" onClick={handleCreateSubcoverages} disabled={createSubcoverages.loading || !subcoveragesFormData.partner_id || subcoveragesFormData.subcoverages.length === 0}>
                        {createSubcoverages.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Création...
                            </>
                        ) : (
                            <>
                                <FiPlus className="me-2" />
                                Créer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>


            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmer la suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir supprimer ce séjour? Cette action est irréversible.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteState.loading}>
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={deleteState.loading}>
                        {deleteState.loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Suppression...
                            </>
                        ) : (
                            'Supprimer'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminTrips;



